import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Eye, Box, FolderPlus, Edit, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDropdown } from '@/hooks/useDropdown';
import type { Collection } from '@maker-toolchest/shared';
import { MAX_COLLECTION_DEPTH } from '@maker-toolchest/shared';

interface CollectionActionsMenuProps {
  collection: Collection;
  onViewModels: () => void;
  onAddSubCollection: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
}

export function CollectionActionsMenu({
  collection,
  onViewModels,
  onAddSubCollection,
  onEdit,
  onDelete,
  isDeleting = false,
}: CollectionActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const menuRef = useDropdown({
    isOpen,
    onClose: () => setIsOpen(false),
  });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - 192, // 192px = w-48 (12rem)
      });
    }
  }, [isOpen]);

  const isAtMaxDepth = collection.depth >= MAX_COLLECTION_DEPTH;

  const handleViewDetails = () => {
    setIsOpen(false);
    navigate(`/collections/${collection.id}`);
  };

  const handleViewModels = () => {
    setIsOpen(false);
    onViewModels();
  };

  const handleAddSubCollection = () => {
    if (isAtMaxDepth) return;
    setIsOpen(false);
    onAddSubCollection();
  };

  const handleEdit = () => {
    setIsOpen(false);
    onEdit();
  };

  const handleDelete = () => {
    setIsOpen(false);
    onDelete();
  };

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        {/* Trigger button */}
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label="Collection actions"
          className="
            flex h-8 w-8 items-center justify-center rounded-md
            text-foreground-muted hover:bg-background-hover hover:text-foreground
            transition-colors
          "
        >
          <MoreVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Dropdown menu - rendered in portal */}
      {isOpen && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
          className="
            w-48 rounded-md border border-border
            bg-background-elevated shadow-lg z-50
          "
        >
          <div className="py-1">
            <button
              onClick={handleViewDetails}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <Eye className="h-4 w-4" aria-hidden="true" />
              View Details
            </button>
            <button
              onClick={handleViewModels}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <Box className="h-4 w-4" aria-hidden="true" />
              View Models
            </button>
            <button
              onClick={handleAddSubCollection}
              role="menuitem"
              disabled={isAtMaxDepth}
              className={`
                flex w-full items-center gap-2 px-4 py-2 text-sm
                ${isAtMaxDepth
                  ? 'opacity-40 cursor-not-allowed text-foreground-muted'
                  : 'text-foreground-muted hover:bg-background-hover hover:text-foreground'
                }
              `}
              title={isAtMaxDepth ? 'Maximum nesting depth reached' : undefined}
            >
              <FolderPlus className="h-4 w-4" aria-hidden="true" />
              Add Sub-collection
            </button>
            <button
              onClick={handleEdit}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <Edit className="h-4 w-4" aria-hidden="true" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              role="menuitem"
              disabled={isDeleting}
              className={`
                flex w-full items-center gap-2 px-4 py-2 text-sm
                ${isDeleting
                  ? 'opacity-40 cursor-not-allowed text-foreground-muted'
                  : 'text-error hover:bg-background-hover'
                }
              `}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Delete
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
